import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  Timestamp,
  addDoc
} from 'firebase/firestore';

export default function IdentityVerificationPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'users'));
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setUsers(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching users:', err);
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const handleApprove = async (uid) => {
    const ref = doc(db, 'users', uid);
    await updateDoc(ref, {
      isVerified: true,
      identityVerificationPending: false,
      identityVerificationApprovedAt: Timestamp.now(),
    });

    await logAction(uid, 'approved');

    setUsers(prev =>
      prev.map(user =>
        user.id === uid
          ? { ...user, isVerified: true, identityVerificationPending: false }
          : user
      )
    );
  };

  const handleReject = async (uid) => {
    const ref = doc(db, 'users', uid);
    await updateDoc(ref, {
      isVerified: false,
      identityVerificationPending: false,
    });

    await logAction(uid, 'rejected');

    setUsers(prev =>
      prev.map(user =>
        user.id === uid
          ? { ...user, isVerified: false, identityVerificationPending: false }
          : user
      )
    );
  };

  const handleDeleteVerification = async (uid) => {
    const ref = doc(db, 'users', uid);
    await updateDoc(ref, {
      identityVerificationPending: false,
      identityVerificationApprovedAt: null,
      identityDocumentType: null,
      identityIDUploadURL: null,
      identitySelfieUploadURL: null,
      identityVerificationSubmittedAt: null,
      isVerified: false
    });

    await logAction(uid, 'deleted');

    setUsers(prev =>
      prev.map(user =>
        user.id === uid
          ? {
              ...user,
              isVerified: false,
              identityVerificationPending: false,
              identityVerificationApprovedAt: null,
              identityDocumentType: null,
              identityIDUploadURL: null,
              identitySelfieUploadURL: null,
              identityVerificationSubmittedAt: null
            }
          : user
      )
    );
  };

  const logAction = async (uid, action) => {
    try {
      const currentUser = auth.currentUser;
      await addDoc(collection(db, `identityVerificationHistory/${uid}/logs`), {
        action,
        timestamp: Timestamp.now(),
        adminUid: currentUser?.uid || 'unknown'
      });
    } catch (err) {
      console.error('❌ Failed to log identity verification action:', err);
    }
  };

  const filtered = users.filter(
    (u) =>
      (u.identityVerificationPending || u.isVerified) &&
      (u.email?.toLowerCase().includes(filter.toLowerCase()) ||
        u.name?.toLowerCase().includes(filter.toLowerCase()))
  );

  return (
    <div style={styles.container}>
      <button onClick={() => navigate('/dashboard')} style={styles.backButton}>
        ← Back to Dashboard
      </button>
      <h2 style={styles.title}>🪪 Identity Verification</h2>

      <input
        type="text"
        placeholder="Search by name or email"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        style={styles.search}
      />

      {loading ? (
        <p>Loading...</p>
      ) : filtered.length === 0 ? (
        <p>No verification records found.</p>
      ) : (
        <div style={styles.grid}>
          {filtered.map((user) => (
            <div key={user.id} style={styles.card}>
              <div style={styles.meta}>
                <strong>Name:</strong> {user.name || 'N/A'}<br />
                <strong>Email:</strong> {user.email || 'N/A'}<br />
                <strong>ID Type:</strong> {user.identityDocumentType || '—'}<br />
                <strong>Status:</strong>{' '}
                {user.identityVerificationPending
                  ? '⏳ Pending'
                  : user.isVerified
                  ? '✅ Verified'
                  : '❌ Not Verified'}<br />
                <strong>Submitted:</strong>{' '}
                {user.identityVerificationSubmittedAt?.toDate?.().toLocaleString() || '—'}
              </div>

              <div style={styles.imageWrap}>
                {user.identityIDUploadURL && (
                  <a href={user.identityIDUploadURL} target="_blank" rel="noopener noreferrer">
                    <img
                      src={user.identityIDUploadURL}
                      alt="ID"
                      style={styles.imageThumb}
                    />
                  </a>
                )}
                {user.identitySelfieUploadURL && (
                  <a href={user.identitySelfieUploadURL} target="_blank" rel="noopener noreferrer">
                    <img
                      src={user.identitySelfieUploadURL}
                      alt="Selfie"
                      style={styles.imageThumb}
                    />
                  </a>
                )}
              </div>

              <div style={styles.buttonRow}>
                {user.identityVerificationPending && (
                  <>
                    <button onClick={() => handleApprove(user.id)} style={styles.approve}>
                      Approve
                    </button>
                    <button onClick={() => handleReject(user.id)} style={styles.reject}>
                      Reject
                    </button>
                  </>
                )}

                {!user.identityVerificationPending && (
                  <button
                    onClick={() => {
                      if (
                        window.confirm(
                          'Are you sure you want to delete this identity verification record? This action is permanent and should only be done upon user request.'
                        )
                      ) {
                        handleDeleteVerification(user.id);
                      }
                    }}
                    style={styles.delete}
                  >
                    Delete Verification
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: '2rem',
    fontFamily: 'system-ui, sans-serif',
    backgroundColor: '#f9f9f9',
    minHeight: '100vh',
  },
  backButton: {
    backgroundColor: '#eeeeee',
    border: '1px solid #ccc',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 500,
    marginBottom: '1rem',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#065f46',
    marginBottom: '1rem',
  },
  search: {
    marginBottom: '1.5rem',
    padding: '0.5rem',
    fontSize: '0.95rem',
    width: '100%',
    maxWidth: '360px',
    border: '1px solid #ccc',
    borderRadius: '6px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '1rem',
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '1rem',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  meta: {
    fontSize: '0.875rem',
    color: '#333',
    marginBottom: '0.5rem',
    lineHeight: '1.4',
  },
  imageWrap: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap',
    marginBottom: '0.5rem',
  },
  imageThumb: {
    width: '80px',
    height: '80px',
    objectFit: 'cover',
    borderRadius: '4px',
    border: '1px solid #ddd',
  },
  buttonRow: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap',
    marginTop: '0.5rem',
  },
  approve: {
    backgroundColor: '#10b981',
    color: '#fff',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 500,
  },
  reject: {
    backgroundColor: '#ef4444',
    color: '#fff',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 500,
  },
  delete: {
    backgroundColor: '#f59e0b', // amber-500
    color: '#fff',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '6px',
    cursor: 'pointer',
    fontWeight: 500,
  },
};
